import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Button, Tag, Space, Row, Col,
  Typography, Collapse, Empty, Spin,
} from 'antd';
import { staticApi } from '../../utils/staticApi';
import {
  FilePdfOutlined, DownloadOutlined, CalendarOutlined,
  DollarOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../../store/condoStore';
import { useAuthStore } from '../../store/authStore';
import { useAuth } from '../../../../context/UnifiedAuthContext';
import { Statement, Fee } from '../../types';
// Sprint 146 (R14-4 / R14-11, ADR-141) — the resident-facing twin of the
// manager Statements page carried BOTH defects: it re-derived its line items
// with `f.period === statement.period` (so an app-created fee, whose period is
// null, itemised as nothing) and rendered every money value from a bare `$` +
// toFixed(2) template. Lines now come from `statement.fees`, the server's own
// list; money goes through the one en-CA/CAD helper.
import { currency } from '../../utils/currency';

declare module 'jspdf' {
  interface jsPDF { lastAutoTable: { finalY: number }; }
}

const { Title, Text } = Typography;
const { Panel } = Collapse;

// ─── PDF Generator ────────────────────────────────────────────────────────────

async function generateOwnerStatementPDF(
  statement: Statement,
  owner: any,
  condoName: string,
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });

  const INDIGO  = [79,  70,  229] as [number, number, number];
  const INK     = [17,  24,  39]  as [number, number, number];
  const SLATE   = [100, 116, 139] as [number, number, number];
  const BORDER  = [229, 231, 235] as [number, number, number];
  const SURFACE = [248, 249, 255] as [number, number, number];
  const WHITE   = [255, 255, 255] as [number, number, number];
  const GREEN   = [5,   150, 105] as [number, number, number];
  const RED     = [220, 38,  38]  as [number, number, number];
  const AMBER   = [217, 119, 6]   as [number, number, number];

  const PAGE_W = 210;
  const PAGE_H = 297;
  const M      = 18;
  const CW     = PAGE_W - M * 2;

  // Top accent rule
  doc.setFillColor(...INDIGO);
  doc.rect(0, 0, PAGE_W, 3, 'F');

  // Header
  let y = 12;
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text(condoName, M, y);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text('Property Management', M, y + 5.5);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INDIGO);
  doc.text('ACCOUNT STATEMENT', PAGE_W - M, y, { align: 'right' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text(`Period: ${statement.period}`, PAGE_W - M, y + 6, { align: 'right' });
  doc.text(`Issued: ${dayjs().format('MMMM D, YYYY')}`, PAGE_W - M, y + 11, { align: 'right' });

  // Divider
  y = 30;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(M, y, PAGE_W - M, y);

  // Recipient block
  y = 36;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...SLATE);
  doc.text('PREPARED FOR', M, y);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text(owner?.name || '—', M, y + 6);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text(`Unit ${owner?.unitNumber || '—'}`, M, y + 12);
  if (owner?.email) doc.text(owner.email, M, y + 17);

  // Statement metadata (right side)
  const metaX = 130;
  const metaRows: [string, string][] = [
    ['Statement ID', String(statement.id || '').slice(0, 20)],
    ['Period',       statement.period || '—'],
    ['Due Date',     statement.dueDate ? dayjs(statement.dueDate).format('MMM D, YYYY') : '—'],
    ['Generated',    dayjs().format('MMM D, YYYY')],
  ];

  metaRows.forEach((row, i) => {
    const ry = y + i * 6.5;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text(row[0], metaX, ry);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...INK);
    doc.text(row[1], PAGE_W - M, ry, { align: 'right' });
  });

  y = 66;
  doc.setDrawColor(...BORDER);
  doc.line(M, y, PAGE_W - M, y);

  // Summary metric cards
  y = 72;
  const cardW = (CW - 8) / 3;
  const balanceColor: [number, number, number] = statement.currentBalance > 0 ? RED : GREEN;

  const cards = [
    { label: 'PREVIOUS BALANCE',  value: currency(statement.previousBalance), accent: INDIGO },
    { label: 'PAYMENTS RECEIVED', value: currency(statement.payments),        accent: GREEN  },
    { label: 'BALANCE DUE',       value: currency(statement.currentBalance),  accent: balanceColor },
  ];

  cards.forEach((card, i) => {
    const cx = M + i * (cardW + 4);
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(cx, y, cardW, 22, 1.5, 1.5, 'FD');
    doc.setFillColor(...card.accent);
    doc.roundedRect(cx, y, cardW, 3, 1.5, 1.5, 'F');
    doc.rect(cx, y + 1.5, cardW, 1.5, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...SLATE);
    doc.text(card.label, cx + cardW / 2, y + 9, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(card.accent[0], card.accent[1], card.accent[2]);
    doc.text(card.value, cx + cardW / 2, y + 18, { align: 'center' });
  });

  y = 100;

  // Fee details heading
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Fee Details', M, y);
  doc.setDrawColor(...INDIGO);
  doc.setLineWidth(0.5);
  doc.line(M, y + 1.5, M + 24, y + 1.5);
  y += 5;

  // R14-4 — the server's itemisation, not a client re-derivation.
  const ownerFees: any[] = Array.isArray((statement as any).fees) ? (statement as any).fees : [];

  const statusColor = (status: string): [number, number, number] => {
    if (status === 'PAID')    return GREEN;
    if (status === 'OVERDUE') return RED;
    return AMBER;
  };

  if (ownerFees.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Description', 'Type', 'Due Date', 'Amount', 'Status']],
      body: ownerFees.map((f: any) => [
        f.description || '—',
        (f.type || '—').replace(/-/g, ' '),
        f.dueDate ? dayjs(f.dueDate).format('MMM D, YYYY') : '—',
        currency(f.amount),
        f.status || '—',
      ]),
      margin: { left: M, right: M },
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        textColor: INK,
        lineColor: BORDER,
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: SURFACE,
        textColor: INDIGO,
        fontStyle: 'bold',
        fontSize: 7.5,
        lineColor: BORDER,
        lineWidth: 0.2,
      },
      alternateRowStyles: { fillColor: [252, 252, 254] as [number, number, number] },
      columnStyles: {
        0: { cellWidth: 56 },
        1: { cellWidth: 30 },
        2: { cellWidth: 32 },
        3: { cellWidth: 28, halign: 'right' as const },
        4: { cellWidth: 28 },
      },
      didParseCell: (data) => {
        if (data.column.index === 4 && data.section === 'body') {
          const col = statusColor(String(data.cell.raw || ''));
          data.cell.styles.textColor = col;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...SLATE);
    doc.text('No fee details available for this period.', M, y + 6);
    y += 16;
  }

  // Balance summary (right-aligned)
  const totalFees = ownerFees.reduce((s: number, f: any) => s + Number(f.amount), 0);
  const summaryRows: [string, string, boolean][] = [
    ['Previous Balance',   currency(statement.previousBalance), false],
    ['Total Fees Charged', currency(totalFees),                 false],
    ['Payments Received',  `(${currency(statement.payments)})`, false],
    ['Balance Due',        currency(statement.currentBalance),  true ],
  ];

  const summaryX = PAGE_W - M - 80;
  const summaryW = 80;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...INK);
  doc.text('Account Summary', summaryX, y);
  doc.setDrawColor(...INDIGO);
  doc.setLineWidth(0.5);
  doc.line(summaryX, y + 1.5, summaryX + 38, y + 1.5);
  y += 5;

  doc.setFillColor(...WHITE);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.25);
  doc.roundedRect(summaryX, y, summaryW, summaryRows.length * 7 + 2, 1.5, 1.5, 'FD');

  summaryRows.forEach((row, i) => {
    const ry = y + 5 + i * 7;
    const isLast = row[2];
    if (isLast) {
      doc.setFillColor(...SURFACE);
      doc.rect(summaryX, ry - 4, summaryW, 7, 'F');
      doc.setDrawColor(...INDIGO);
      doc.setLineWidth(0.2);
      doc.line(summaryX, ry - 4, summaryX + summaryW, ry - 4);
    }
    doc.setFontSize(isLast ? 8.5 : 8);
    doc.setFont('helvetica', isLast ? 'bold' : 'normal');
    doc.setTextColor(
      isLast ? INK[0] : SLATE[0],
      isLast ? INK[1] : SLATE[1],
      isLast ? INK[2] : SLATE[2]
    );
    doc.text(row[0], summaryX + 4, ry);
    const valColor = isLast ? (statement.currentBalance > 0 ? RED : GREEN) : INK;
    doc.setTextColor(...valColor);
    doc.setFont('helvetica', isLast ? 'bold' : 'normal');
    doc.text(row[1], summaryX + summaryW - 4, ry, { align: 'right' });
  });

  y += summaryRows.length * 7 + 10;

  // Payment due notice
  if (statement.currentBalance > 0) {
    doc.setFillColor(254, 252, 232);
    doc.setDrawColor(253, 230, 138);
    doc.setLineWidth(0.2);
    doc.roundedRect(M, y, CW, 12, 1.5, 1.5, 'FD');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...AMBER);
    doc.text('Payment Due', M + 4, y + 5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 80, 10);
    doc.text(
      `Please remit ${currency(statement.currentBalance)} by the due date shown above. Contact management for payment options.`,
      M + 28, y + 5
    );
    y += 18;
  }

  // Footer
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(M, PAGE_H - 16, PAGE_W - M, PAGE_H - 16);
  doc.setFillColor(...INDIGO);
  doc.circle(M, PAGE_H - 16, 0.6, 'F');
  doc.circle(PAGE_W - M, PAGE_H - 16, 0.6, 'F');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...SLATE);
  doc.text(`${condoName}  ·  Statement for ${statement.period}`, M, PAGE_H - 9);
  doc.text(
    `Generated ${dayjs().format('YYYY-MM-DD')}  ·  Page 1 of 1`,
    PAGE_W - M, PAGE_H - 9, { align: 'right' }
  );

  doc.save(
    `Statement_${owner?.name?.replace(/\s/g, '_') || 'Unknown'}_${statement.period}_${dayjs().format('YYYYMMDD')}.pdf`
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  // Handle "YYYY-MM" → "Month YYYY"
  if (/^\d{4}-\d{2}$/.test(period)) {
    return dayjs(`${period}-01`).format('MMMM YYYY');
  }
  // Handle "YYYY-Q#" → "Q# YYYY"
  const quarterMatch = period.match(/^(\d{4})-Q(\d)$/);
  if (quarterMatch) {
    return `Q${quarterMatch[2]} ${quarterMatch[1]}`;
  }
  return period;
}

// ─── Fee Row inside the collapsible panel ─────────────────────────────────────

function FeeRow({ fee }: { fee: Fee }) {
  const statusStyle: Record<string, React.CSSProperties> = {
    PAID:    { background: '#ecfdf5', color: '#059669', border: '1px solid #bbf7d0' },
    OVERDUE: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
    PENDING: { background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' },
  };
  const style = statusStyle[fee.status] ?? statusStyle.PENDING;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      borderBottom: '1px solid #f3f4f6',
      gap: 8,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ display: 'block', fontSize: 13, color: '#111827' }}>
          {fee.description || '—'}
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {(fee.type || '').replace(/-/g, ' ')}
          {fee.dueDate ? ` · Due ${dayjs(fee.dueDate).format('MMM D, YYYY')}` : ''}
        </Text>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <Text strong style={{ fontSize: 14, color: '#111827' }}>
          {currency(fee.amount)}
        </Text>
        <Tag style={{ borderRadius: 20, fontSize: 11, padding: '1px 10px', fontWeight: 600, ...style }}>
          {fee.status}
        </Tag>
      </div>
    </div>
  );
}

// ─── Statement Card ───────────────────────────────────────────────────────────

interface StatementCardProps {
  statement: Statement;
  owner: any;
  condoName: string;
  fees: Fee[];
}

function StatementCard({ statement, owner, condoName, fees }: StatementCardProps) {
  // R14-4 — the server's itemisation (see the module header).
  const stmtFees: Fee[] = Array.isArray((statement as any).fees) ? (statement as any).fees : [];

  const balanceColor = statement.currentBalance > 0 ? '#dc2626' : '#059669';
  const balanceBg    = statement.currentBalance > 0 ? '#fef2f2' : '#ecfdf5';

  const handleDownload = async () => {
    await generateOwnerStatementPDF(statement, owner, condoName);
    staticApi.message.success('Statement downloaded');
  };

  return (
    <Card
      style={{
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        height: '100%',
      }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Top accent stripe */}
      <div style={{ height: 4, background: 'linear-gradient(90deg, #4f46e5, #6366f1)' }} />

      {/* Card header */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <CalendarOutlined style={{ color: '#6366f1', fontSize: 14 }} />
              <Text strong style={{ fontSize: 16, color: '#1e1b4b' }}>
                {formatPeriod(statement.period)}
              </Text>
            </div>
            {statement.dueDate && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Due: {dayjs(statement.dueDate).format('MMMM D, YYYY')}
              </Text>
            )}
          </div>

          {/* Status badge */}
          {statement.isSent ? (
            <Tag
              icon={<CheckCircleOutlined />}
              style={{
                borderRadius: 20, fontSize: 12, padding: '2px 12px',
                background: '#ecfdf5', color: '#059669', border: '1px solid #bbf7d0',
                fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              Sent
            </Tag>
          ) : (
            <Tag
              icon={<ClockCircleOutlined />}
              style={{
                borderRadius: 20, fontSize: 12, padding: '2px 12px',
                background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb',
                fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              Draft
            </Tag>
          )}
        </div>

        {/* Balance due — large featured number */}
        <div style={{
          background: balanceBg,
          border: `1px solid ${statement.currentBalance > 0 ? '#fecaca' : '#bbf7d0'}`,
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <DollarOutlined style={{ fontSize: 20, color: balanceColor }} />
          <div>
            <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Balance Due
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: balanceColor, lineHeight: 1.1 }}>
              {currency(statement.currentBalance)}
            </div>
          </div>
        </div>
      </div>

      {/* Secondary stats */}
      <div style={{
        padding: '12px 20px',
        display: 'flex',
        gap: 20,
        background: '#fafafa',
        borderBottom: '1px solid #f3f4f6',
      }}>
        <div>
          <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Previous Balance
          </div>
          <Text style={{ fontSize: 14, color: '#374151', fontWeight: 600 }}>
            {currency(statement.previousBalance)}
          </Text>
        </div>
        <div style={{ width: 1, background: '#e5e7eb' }} />
        <div>
          <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Payments Made
          </div>
          <Text style={{ fontSize: 14, color: '#059669', fontWeight: 600 }}>
            {currency(statement.payments)}
          </Text>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            Fees on file
          </div>
          <Text style={{ fontSize: 14, color: '#374151', fontWeight: 600 }}>
            {stmtFees.length}
          </Text>
        </div>
      </div>

      {/* View fees expandable + download button */}
      <div style={{ padding: '0 0 4px' }}>
        {stmtFees.length > 0 ? (
          <Collapse
            ghost
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} style={{ color: '#6366f1' }} />
            )}
          >
            <Panel
              key="fees"
              header={
                <Text style={{ color: '#4f46e5', fontSize: 13, fontWeight: 600 }}>
                  View Fees ({stmtFees.length})
                </Text>
              }
            >
              <div style={{ margin: '0 -24px -16px', borderTop: '1px solid #f3f4f6' }}>
                {stmtFees.map((fee) => (
                  <FeeRow key={fee.id} fee={fee} />
                ))}
              </div>
            </Panel>
          </Collapse>
        ) : (
          <div style={{ padding: '12px 20px' }}>
            <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
              No fees recorded for this period.
            </Text>
          </div>
        )}
      </div>

      {/* Download button */}
      <div style={{ padding: '12px 20px 18px', borderTop: '1px solid #f3f4f6' }}>
        <Button
          type="primary"
          size="small"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          style={{
            background: '#4f46e5',
            borderColor: '#4f46e5',
            borderRadius: 8,
            fontWeight: 600,
            height: 34,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          Download PDF
        </Button>
      </div>
    </Card>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const OwnerStatements: React.FC = () => {
  const { statements, fees, owners, fetchStatements, fetchFees } = useCondoStore();
  const { currentCondo, user: storeUser } = useAuthStore();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (currentCondo?.id) {
          await Promise.all([fetchStatements(currentCondo.id), fetchFees(currentCondo.id)]);
        } else {
          await Promise.all([fetchStatements(), fetchFees()]);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetch actions are stable zustand actions
  }, [currentCondo?.id]);

  // Resolve owner record for the logged-in user
  const ownerRecord = useMemo(
    () => owners.find((o) => o.userId === user?.id || o.id === storeUser?.ownerId),
    [owners, user?.id, storeUser?.ownerId]
  );

  // Filter and sort statements
  const myStatements = useMemo<Statement[]>(() => {
    if (!ownerRecord) return [];
    return [...statements]
      .filter(
        (s) => s.ownerId === ownerRecord.id || s.ownerId === (ownerRecord as any).userId
      )
      .sort((a, b) => {
        // Sort by period descending (newest first)
        if (a.period > b.period) return -1;
        if (a.period < b.period) return 1;
        return 0;
      });
  }, [statements, ownerRecord]);

  const totalDue = myStatements.reduce(
    (sum, s) => sum + (s.currentBalance > 0 ? s.currentBalance : 0),
    0
  );

  const condoName = currentCondo?.name || 'Condominium Corporation';

  return (
    <div style={{ padding: '0 4px' }}>

      {/* Page Header */}
      <div style={{
        background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)',
        border: '1px solid #e0e7ff',
        borderRadius: 14,
        padding: '22px 28px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 18,
      }}>
        <div style={{
          width: 52, height: 52,
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          borderRadius: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          flexShrink: 0,
        }}>
          <FilePdfOutlined style={{ color: '#fff', fontSize: 24 }} />
        </div>
        <div>
          <Title level={4} style={{ margin: 0, color: '#1e1b4b', fontWeight: 700, lineHeight: 1.2 }}>
            My Statements
          </Title>
          <Text style={{ color: '#6366f1', fontSize: 13 }}>
            Your financial statements from building management
          </Text>
        </div>
      </div>

      {/* Summary Bar */}
      {!loading && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[
            {
              label: 'Total Statements',
              value: myStatements.length,
              icon: <FilePdfOutlined />,
              color: '#4f46e5',
              bg: '#eef2ff',
              border: '#c7d2fe',
            },
            {
              label: 'Total Balance Due',
              value: currency(totalDue),
              icon: <DollarOutlined />,
              color: totalDue > 0 ? '#dc2626' : '#059669',
              bg: totalDue > 0 ? '#fef2f2' : '#ecfdf5',
              border: totalDue > 0 ? '#fecaca' : '#bbf7d0',
            },
          ].map((m, i) => (
            <Col key={i} xs={24} sm={12}>
              <Card
                size="small"
                style={{
                  borderRadius: 10,
                  border: `1px solid ${m.border}`,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  background: m.bg,
                  overflow: 'hidden',
                }}
                bodyStyle={{ padding: '14px 20px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 9,
                    background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                    color: m.color, fontSize: 17,
                    flexShrink: 0,
                  }}>
                    {m.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {m.label}
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: m.color, lineHeight: 1.1 }}>
                      {m.value}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Content */}
      {loading ? (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: 280, flexDirection: 'column', gap: 16,
        }}>
          <Spin size="large" />
          <Text type="secondary">Loading your statements…</Text>
        </div>
      ) : myStatements.length === 0 ? (
        <Card
          style={{ borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
          bodyStyle={{ padding: '60px 40px' }}
        >
          <Empty
            image={
              <div style={{
                width: 72, height: 72,
                background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto',
              }}>
                <FilePdfOutlined style={{ fontSize: 32, color: '#6366f1' }} />
              </div>
            }
            imageStyle={{ height: 72 }}
            description={
              // Sprint 38 — finding H6. Round-5 saw a bare empty-state
              // with no actionable guidance. Spell out the cadence
              // (so a resident isn't waiting on a button that won't
              // appear) and give them a clear "talk to your manager"
              // path with the condo's contact email if seeded.
              <Space direction="vertical" size={8} style={{ textAlign: 'center', maxWidth: 460, margin: '0 auto' }}>
                <Text strong style={{ fontSize: 16, color: '#374151' }}>No statements generated for your unit yet</Text>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.55 }}>
                  Your property manager generates statements on a quarterly cadence. They will appear here automatically when they're issued — typically within two weeks after each quarter ends.
                </Text>
                {currentCondo?.contactEmail && (
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                    Questions about your account?{' '}
                    <a href={`mailto:${currentCondo.contactEmail}`} style={{ color: '#4f46e5', fontWeight: 600 }}>
                      Contact {currentCondo.name || 'your management team'}
                    </a>
                  </Text>
                )}
              </Space>
            }
          />
        </Card>
      ) : (
        <Row gutter={[20, 20]}>
          {myStatements.map((statement) => (
            <Col key={statement.id} xs={24} md={12}>
              <StatementCard
                statement={statement}
                owner={ownerRecord}
                condoName={condoName}
                fees={fees}
              />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};
